import { Button, FormLayout, TextInput } from 'components/common'
import SwitchField from 'components/bundleProduct/switchField'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { ConfigurationWithId } from 'types/configuration'
import {
  getInputErrorMessage,
  getNestedErrorMessage,
} from 'utils/helpers/getNestedErrorMessage'
import { handleError } from 'utils/helpers/handleError'
import { getPolygonData } from 'utils/api/polygonApi'
import {
  triggerInstashopReconciliationSync,
  updateInstashopLastmileConfig,
} from 'utils/api/configApi'
import { instashopLastmileConfigFields } from './utils'
import {
  instashopLastmileConfigSchema,
  InstashopLastmileConfigFormFields,
} from 'utils/schema/instashopLastmileConfigSchema'
import { yupResolver } from '@hookform/resolvers/yup'

const {
  IS_ENABLED,
  SECRET_KEY,
  API_KEY,
  BASE_URL,
  AGG_INT_WEBHOOK_URL,
  AGG_INT_BEARER_TOKEN,
  GOOGLE_MAP_API_KEY,
  RECONCILIATION_EXPORT_IS_ENABLED,
  RECONCILIATION_EXPORT_CRON_EXPRESSION,
  SFTP_PROTOCOL,
  SFTP_HOST,
  SFTP_PORT,
  SFTP_USERNAME,
  SFTP_PASSWORD,
  SFTP_DIRECTORY,
} = instashopLastmileConfigFields

const manualActionTypes = {
  RECONCILIATION_SYNC: 'RECONCILIATION_SYNC',
  POLYGON_SYNC: 'POLYGON_SYNC',
}

type Props = {
  configuration?: ConfigurationWithId
}

const InstashopLastmileConfigurationForm = ({ configuration }: Props) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<InstashopLastmileConfigFormFields>({
    resolver: yupResolver(instashopLastmileConfigSchema),
    defaultValues: {
      ...(configuration?.instashopLastmile || {}),
      sftp: {
        protocol: 'SFTP',
        ...(configuration?.instashopLastmile?.sftp || {}),
      },
    },
  })

  const [loading, setLoading] = useState(false)
  const [manualAction, setManualAction] = useState({
    type: manualActionTypes.RECONCILIATION_SYNC,
    isLoading: false,
  })

  const handleSave = async (data: InstashopLastmileConfigFormFields) => {
    const configToast = toast.loading('Updating Instashop Lastmile config...')
    setLoading(true)
    try {
      await updateInstashopLastmileConfig({
        ...data,
        sftp: { ...data.sftp, isEnable: true, protocol: 'SFTP' },
      })
      toast.success('Instashop Lastmile config updated successfully.', {
        id: configToast,
      })
    } catch (error) {
      handleError(error, configToast, {
        default: 'Failed to update Instashop Lastmile config.',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleReconciliationSync = async () => {
    setManualAction({
      type: manualActionTypes.RECONCILIATION_SYNC,
      isLoading: true,
    })
    const syncToast = toast.loading('Triggering reconciliation export...')

    try {
      const { data } = await triggerInstashopReconciliationSync()
      toast.success(
        data?.message || 'Reconciliation export triggered successfully.',
        { id: syncToast },
      )
    } catch (error) {
      handleError(error, syncToast, {
        default: 'Failed to trigger reconciliation export.',
      })
    } finally {
      setManualAction({
        type: manualActionTypes.RECONCILIATION_SYNC,
        isLoading: false,
      })
    }
  }

  const handlePolygonSync = async () => {
    setManualAction({ type: manualActionTypes.POLYGON_SYNC, isLoading: true })
    const syncToast = toast.loading('Fetching polygon data...', {
      duration: Infinity,
    })

    try {
      const { data } = await getPolygonData()
      toast.success(data?.message || 'Polygon data fetched successfully', {
        id: syncToast,
        duration: 3000,
      })
    } catch (error) {
      handleError(error, syncToast, {
        default: 'Failed to fetch polygon data.',
      })
    } finally {
      setManualAction({
        type: manualActionTypes.POLYGON_SYNC,
        isLoading: false,
      })
    }
  }

  return (
    <form onSubmit={handleSubmit(handleSave)}>
      {/* ================= INSTASHOP LASTMILE INTEGRATION ================= */}
      <h3 className="title mt-4">Instashop Lastmile Integration</h3>

      <FormLayout>
        <SwitchField
          control={control}
          name={IS_ENABLED.key}
          label={IS_ENABLED.label}
          description="Enable or disable Instashop Lastmile integration"
          showStatusPill
        />

        <div className="mb-5 border-t" />

        <div className="grid gap-4 mid-md:grid-cols-2">
          <TextInput
            required
            register={register}
            name={BASE_URL.key}
            label={BASE_URL.label}
            error={getInputErrorMessage(errors, BASE_URL)}
            placeholder="https://..."
          />

          <TextInput
            required
            type="password"
            register={register}
            name={SECRET_KEY.key}
            label={SECRET_KEY.label}
            error={getInputErrorMessage(errors, SECRET_KEY)}
          />

          <TextInput
            required
            register={register}
            name={API_KEY.key}
            label={API_KEY.label}
            error={getInputErrorMessage(errors, API_KEY)}
          />
        </div>

        <div className="mt-5 flex justify-end">
          <Button
            type="button"
            variant="outline"
            loading={
              manualAction.type === manualActionTypes.POLYGON_SYNC &&
              manualAction.isLoading
            }
            disabled={manualAction.isLoading || loading}
            buttonText="Sync Polygon"
            className="w-48"
            onClick={handlePolygonSync}
          />
        </div>
      </FormLayout>

      {/* ================= AGGREGATOR INTEGRATION ================= */}
      <h3 className="title mt-4">Aggregator Integration</h3>

      <FormLayout>
        <div className="grid gap-4 mid-md:grid-cols-2">
          <TextInput
            required
            register={register}
            name={AGG_INT_WEBHOOK_URL.key}
            label={AGG_INT_WEBHOOK_URL.label}
            error={getInputErrorMessage(errors, AGG_INT_WEBHOOK_URL)}
            placeholder="https://..."
          />

          <TextInput
            required
            type="password"
            register={register}
            name={AGG_INT_BEARER_TOKEN.key}
            label={AGG_INT_BEARER_TOKEN.label}
            error={getInputErrorMessage(errors, AGG_INT_BEARER_TOKEN)}
          />
        </div>
      </FormLayout>

      {/* ================= GOOGLE MAPS API ================= */}
      <h3 className="title mt-4">Google Maps API</h3>

      <FormLayout>
        <div className="grid gap-4 mid-md:grid-cols-2">
          <TextInput
            required
            register={register}
            name={GOOGLE_MAP_API_KEY.key}
            label={GOOGLE_MAP_API_KEY.label}
            error={getInputErrorMessage(errors, GOOGLE_MAP_API_KEY)}
          />
        </div>
      </FormLayout>

      {/* ================= RECONCILIATION SFTP EXPORT ================= */}
      <h3 className="title mt-4">Reconciliation SFTP Export</h3>

      <FormLayout>
        <SwitchField
          control={control}
          name={RECONCILIATION_EXPORT_IS_ENABLED.key}
          label={RECONCILIATION_EXPORT_IS_ENABLED.label}
          description="Enable or disable the reconciliation SFTP export job"
          showStatusPill
        />

        <div className="mb-5 border-t" />

        <div className="grid items-end gap-4 mid-md:grid-cols-2">
          <TextInput
            required
            register={register}
            name={RECONCILIATION_EXPORT_CRON_EXPRESSION.key}
            label={RECONCILIATION_EXPORT_CRON_EXPRESSION.label}
            error={getInputErrorMessage(
              errors,
              RECONCILIATION_EXPORT_CRON_EXPRESSION,
            )}
            placeholder="*/2 * * * *"
          />

          <Button
            type="button"
            variant="outline"
            loading={
              manualAction.type === manualActionTypes.RECONCILIATION_SYNC &&
              manualAction.isLoading
            }
            disabled={manualAction.isLoading || loading}
            buttonText="Trigger Reconciliation Export"
            className="w-64"
            onClick={handleReconciliationSync}
          />
        </div>

        <div className="my-5 border-t" />

        <div className="grid gap-4 mid-md:grid-cols-2">
          <TextInput
            disabled
            register={register}
            name={`${SFTP_PROTOCOL.parentKey}.${SFTP_PROTOCOL.key}`}
            label={SFTP_PROTOCOL.label}
          />

          <TextInput
            required
            register={register}
            name={`${SFTP_HOST.parentKey}.${SFTP_HOST.key}`}
            label={SFTP_HOST.label}
            error={getNestedErrorMessage(errors, SFTP_HOST)}
          />

          <TextInput
            required
            type="number"
            min={1}
            max={65535}
            register={register}
            name={`${SFTP_PORT.parentKey}.${SFTP_PORT.key}`}
            label={SFTP_PORT.label}
            error={getNestedErrorMessage(errors, SFTP_PORT)}
            placeholder="e.g., 22"
          />

          <TextInput
            required
            register={register}
            name={`${SFTP_USERNAME.parentKey}.${SFTP_USERNAME.key}`}
            label={SFTP_USERNAME.label}
            error={getNestedErrorMessage(errors, SFTP_USERNAME)}
          />

          <TextInput
            required
            type="password"
            register={register}
            name={`${SFTP_PASSWORD.parentKey}.${SFTP_PASSWORD.key}`}
            label={SFTP_PASSWORD.label}
            error={getNestedErrorMessage(errors, SFTP_PASSWORD)}
          />

          <TextInput
            required
            register={register}
            name={`${SFTP_DIRECTORY.parentKey}.${SFTP_DIRECTORY.key}`}
            label={SFTP_DIRECTORY.label}
            error={getNestedErrorMessage(errors, SFTP_DIRECTORY)}
          />
        </div>
      </FormLayout>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button loading={loading} buttonText="Save" className="w-32" />
      </div>
    </form>
  )
}

export default InstashopLastmileConfigurationForm
